import { FC, useRef } from 'react';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useInView } from 'react-intersection-observer';
import Carousel from 'react-multi-carousel';
import ArrowCircleButton from 'src/components/atoms/arrowCircleButton';
import { ICoverPhoto } from 'src/interfaces/app';

import InteractiveCoverImage from './interactiveCoverImage';
import CoverPhotoStyle from './style';

import { useGtmV2 } from '@hooks/dataTracking/useGtmV2';

type Props = {
  coverPhotos: ICoverPhoto[];
  loading: boolean;
};

const CoverPhoto: FC<Props> = ({ coverPhotos = [], loading }) => {
  const theme = useTheme();
  const { sendGtmClickBanner } = useGtmV2();

  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const responsive = {
    largeDesktop: {
      breakpoint: { max: 6000, min: theme.breakpoints.values.xl },
      items: 1,
    },
    desktop: {
      breakpoint: { max: theme.breakpoints.values.xl, min: theme.breakpoints.values.lg },
      items: 1,
    },
    tablet: {
      breakpoint: { max: theme.breakpoints.values.lg, min: theme.breakpoints.values.sm },
      items: 1,
    },
    mobile: {
      breakpoint: { max: theme.breakpoints.values.sm, min: 0 },
      items: 1,
    },
  };

  const matches = useMediaQuery(`(min-width:${theme.breakpoints.values.lg}px)`);
  // eslint-disable-next-line no-undef
  let buttonPressTimer: NodeJS.Timeout;
  let isClick = true;
  let touchStartX = 0;
  let touchStartY = 0;
  let hasMoved = false;
  const SWIPE_THRESHOLD = 50;
  const CLICK_THRESHOLD = 10;
  const coverPhotoClickRef = useRef<boolean>(false);

  const coverPhotoTouchStart = (e?: React.TouchEvent | React.MouseEvent) => {
    hasMoved = false;
    isClick = true;

    if (e) {
      if ('touches' in e) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      } else {
        touchStartX = e.clientX;
        touchStartY = e.clientY;
      }
    }

    buttonPressTimer = setTimeout(() => {
      isClick = false;
    }, 300);
  };

  const coverPhotoTouchMove = (e?: React.TouchEvent | React.MouseEvent) => {
    if (!hasMoved && e) {
      let currentX, currentY;

      if ('touches' in e) {
        currentX = e.touches[0].clientX;
        currentY = e.touches[0].clientY;
      } else {
        currentX = e.clientX;
        currentY = e.clientY;
      }

      const deltaX = Math.abs(currentX - touchStartX);
      const deltaY = Math.abs(currentY - touchStartY);

      if (deltaX > CLICK_THRESHOLD || deltaY > CLICK_THRESHOLD) {
        hasMoved = true;
        isClick = false;
        clearTimeout(buttonPressTimer);
      }
    }
  };

  const coverPhotoTouchEnd = (item: ICoverPhoto) => {
    clearTimeout(buttonPressTimer);

    if (isClick && !hasMoved && !coverPhotoClickRef.current) {
      coverPhotoClickRef.current = true;
      sendGtmClickBanner({
        banner_information: {
          banner_id: item.id,
          baner_name: item.alt,
          banner_type: item.link_object_type,
          item_index: coverPhotos.findIndex((coverPhoto) => coverPhoto.id === item.id),
          max_index: coverPhotos.length - 1,
        },
      });

      setTimeout(() => {
        coverPhotoClickRef.current = false;
      }, 5000);
      window.open(item.link, item.target);
    }

    isClick = true;
    hasMoved = false;
  };

  return (
    <CoverPhotoStyle ref={ref}>
      {inView && (
        <Carousel
          responsive={responsive}
          centerMode={matches}
          infinite
          removeArrowOnDeviceType={['mobile', 'tablet']}
          autoPlay={!loading}
          autoPlaySpeed={7000}
          pauseOnHover
          draggable
          swipeable
          minimumTouchDrag={SWIPE_THRESHOLD}
          containerClass="carousel-container"
          itemClass="carousel-img-li"
          ssr
          customRightArrow={<ArrowCircleButton direction="right" className="carousel-arrow-right" />}
          customLeftArrow={<ArrowCircleButton direction="left" className="carousel-arrow-left" />}
        >
          {coverPhotos.map((item: ICoverPhoto) => (
            <InteractiveCoverImage
              key={item.id}
              item={item}
              coverPhotoTouchStart={coverPhotoTouchStart}
              coverPhotoTouchMove={coverPhotoTouchMove}
              coverPhotoTouchEnd={coverPhotoTouchEnd}
            />
          ))}
        </Carousel>
      )}
    </CoverPhotoStyle>
  );
};

export default CoverPhoto;
